import { useTabsStore } from '@shared/stores/tabs.store';
import type { CrmTab } from '@shared/stores/tabs.store';
import { useUIStore } from '@shared/stores/ui.store';

import { useComposerAttachmentsStore } from './store/composer-attachments.store';
import {
  conversationById,
  isBlankConversation,
  useConversationsStore,
} from './store/conversations.store';
import { chatIdFromTabPath } from './tab-binding';

const AI_CIO_PATH = '/ai-cio';

function isAiCioPage(tab: CrmTab): boolean {
  return tab.path.split('?')[0] === AI_CIO_PATH;
}

function activeChatStaysOnScreen(next: CrmTab | undefined, switchesTab: boolean): boolean {
  if (!next) return true;
  if (switchesTab && chatIdFromTabPath(next.path) !== null) return false;
  return isAiCioPage(next) || useUIStore.getState().aiPanelOpen;
}

function discardDraftsLeftBehind(
  closed: CrmTab[],
  tabs: CrmTab[],
  activeTabId: string,
  prevActiveTabId: string,
): void {
  const shown = new Set(tabs.map((tab) => chatIdFromTabPath(tab.path)));
  const next = tabs.find((tab) => tab.id === activeTabId);
  const keepActive = activeChatStaysOnScreen(next, activeTabId !== prevActiveTabId);
  const { conversations, activeId } = useConversationsStore.getState();
  for (const tab of closed) {
    const ids = new Set([chatIdFromTabPath(tab.path)]);
    if (tab.id === prevActiveTabId && isAiCioPage(tab)) ids.add(activeId);
    for (const id of ids) {
      const chat = id ? conversationById(conversations, id) : undefined;
      if (!id || !chat || !isBlankConversation(chat) || shown.has(id)) continue;
      if (id === activeId && keepActive) continue;
      useComposerAttachmentsStore.getState().clear(id);
    }
  }
}

export function watchClosedChatTabs(): () => void {
  return useTabsStore.subscribe((state, prev) => {
    if (state.tabs === prev.tabs) return;
    const open = new Set(state.tabs.map((tab) => tab.id));
    const closed = prev.tabs.filter((tab) => !open.has(tab.id));
    if (closed.length === 0) return;
    discardDraftsLeftBehind(closed, state.tabs, state.activeTabId, prev.activeTabId);
  });
}
